import { forwardRef, useEffect, useImperativeHandle, useRef } from "react";
import QRCodeStyling from "qr-code-styling";

function buildOptions(data, design, size) {
  return {
    width: size,
    height: size,
    type: "canvas",
    data: data || " ",
    margin: 8,
    qrOptions: { errorCorrectionLevel: "H" },
    dotsOptions: { color: design.fgColor, type: design.dotType },
    backgroundOptions: { color: design.bgColor },
    cornersSquareOptions: { type: design.cornerSquareType, color: design.fgColor },
    cornersDotOptions: { type: design.cornerDotType, color: design.fgColor },
    image: design.logo || undefined,
    imageOptions: { crossOrigin: "anonymous", margin: 6, imageSize: 0.38, hideBackgroundDots: true },
  };
}

// Renders the QR as an <img> managed by React (only the src is set imperatively).
// This isolates qr-code-styling's DOM output from React's reconciliation, avoiding
// "removeChild NotFoundError" crashes when the preview updates or unmounts.
export const QRPreview = forwardRef(({ data, design, size = 280 }, ref) => {
  const imgRef = useRef(null);
  const qrRef = useRef(null);
  const urlRef = useRef(null);

  useEffect(() => {
    let cancelled = false;
    // Recreate the instance on every change so the raster fully reflects the
    // latest dot/corner/color styling (update() can keep stale internal state).
    const instance = new QRCodeStyling(buildOptions(data, design, size));
    qrRef.current = instance;
    instance
      .getRawData("png")
      .then((blob) => {
        if (cancelled || !blob) return;
        const nextUrl = URL.createObjectURL(blob);
        if (urlRef.current) URL.revokeObjectURL(urlRef.current);
        urlRef.current = nextUrl;
        if (imgRef.current) imgRef.current.src = nextUrl;
      })
      .catch(() => {});
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data, JSON.stringify(design), size]);

  useEffect(() => {
    return () => {
      if (urlRef.current) URL.revokeObjectURL(urlRef.current);
    };
  }, []);

  useImperativeHandle(ref, () => ({
    download: (ext = "png", name = "qrcode") => {
      qrRef.current?.download({ name, extension: ext });
    },
  }));

  return (
    <img
      ref={imgRef}
      data-testid="qr-preview"
      alt="QR code"
      width={size}
      height={size}
      style={{ width: size, height: size, display: "block" }}
      className="transition-opacity duration-300"
    />
  );
});

QRPreview.displayName = "QRPreview";
